import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  ReactNode,
} from 'react';
import '../css/notificationSidebarContext.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type NotificationSidebarContextValue = {
  isOpen:  boolean;
  open:    (content: ReactNode) => void;
  close:   () => void;
  toggle:  (content: ReactNode) => void;
};

// ─── Context ──────────────────────────────────────────────────────────────────

const NotificationSidebarCtx = createContext<NotificationSidebarContextValue | null>(null);

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useNotificationSidebar = (): NotificationSidebarContextValue => {
  const ctx = useContext(NotificationSidebarCtx);
  if (!ctx) throw new Error('useNotificationSidebar must be used within NotificationSidebarProvider');
  return ctx;
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const NotificationSidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen,  setIsOpen]  = useState(false);
  const [content, setContent] = useState<ReactNode>(null);

  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const lockScroll = useCallback(() => {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.overflow = 'hidden';
  document.body.style.paddingRight = `${scrollbarWidth}px`;
}, []);

const unlockScroll = useCallback(() => {
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
}, []);

useEffect(() => {
  return () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };
}, []);

const close = useCallback(() => {
  setIsOpen(false);
  unlockScroll();
}, [unlockScroll]);

const open = useCallback((newContent: ReactNode) => {
  setContent(newContent);
  setIsOpen(true);
  requestAnimationFrame(() => lockScroll());
}, [lockScroll]);

const toggle = useCallback((newContent: ReactNode) => {
  if (isOpenRef.current) {
    setIsOpen(false);
    unlockScroll();
  } else {
    setContent(newContent);
    setIsOpen(true);
    requestAnimationFrame(() => lockScroll());
  }
}, [lockScroll, unlockScroll]);

  const value = useMemo<NotificationSidebarContextValue>(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return (
    <NotificationSidebarCtx.Provider value={value}>
      {children}

      {/* ── Backdrop ── */}
      <div //NOSONAR
        className={`scl--notif-sidebar-backdrop ${isOpen ? 'active' : ''}`}
        onClick={close}
      />

      {/* ── Sidebar panel — no header, NotificationSidebar owns its header ── */}
      <aside //NOSONAR
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        className={`scl--notif-sidebar-panel ${isOpen ? 'active' : ''}`}
      >
        <section className="scl--notif-sidebar-content">
          {content}
        </section>
      </aside>
    </NotificationSidebarCtx.Provider>
  );
};